import {
	Box,
	Button,
	FormControl,
	Grid,
	MenuItem,
	Select,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";

import { format, parse } from "date-fns";
import csLocale from "date-fns/locale/cs";
import deLocale from "date-fns/locale/de";
import atLocale from "date-fns/locale/de-AT";
import enLocale from "date-fns/locale/en-GB";
import esLocale from "date-fns/locale/es";
import frLocale from "date-fns/locale/fr";
import itLocale from "date-fns/locale/it";
import ltLocale from "date-fns/locale/lt";
import nlLocale from "date-fns/locale/nl";
import beLocale from "date-fns/locale/nl-BE";
import plLocale from "date-fns/locale/pl";
import roLocale from "date-fns/locale/ro";
import skLocale from "date-fns/locale/sk";

import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import YearMonthRangePicker from "../../../../components/YearMonthRangePicker";
import { useGetCountryCodesQuery } from "../../../cargo/cargoApiSlice";
import { useGetAdminWeeklyPublicationStatsQuery } from "../../statisticsApiSlice";

const today = new Date();

const localeMap = {
	at: atLocale,
	be: beLocale,
	cz: csLocale,
	de: deLocale,
	en: enLocale,
	es: esLocale,
	fr: frLocale,
	it: itLocale,
	lt: ltLocale,
	nl: nlLocale,
	pl: plLocale,
	ro: roLocale,
	sk: skLocale,
};

export const WeeklyPublicationStats = ({ companyId }) => {
	const { i18n } = useTranslation();
	const locale = i18n.language || "pl";
	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: 10, // Number of rows per page
	});

	const [country, setCountry] = useState(companyId ? "" : "PL");
	const [rangeValue, setRangeValue] = useState([today, today]);
	const [filterValues, setFilterValues] = useState({
		company: companyId,
		country: country,
		range: rangeValue.map((date) => {
			const formatter = new Intl.DateTimeFormat("en", {
				month: "2-digit",
				year: "numeric",
			});
			const [month, year] = formatter.format(date).split("/");
			return `${month}_${year}`;
		}),
		perPage: pagination.pageSize,
		page: pagination.pageIndex,
	});

	const {
		data: stats,
		error,
		isLoading,
		isFetching,
		// refetch,
	} = useGetAdminWeeklyPublicationStatsQuery(filterValues);

	const handleSubmit = async () => {
		const parsedRange = rangeValue.map((date) => {
			const formatter = new Intl.DateTimeFormat("en", {
				month: "2-digit",
				year: "numeric",
			});
			const [month, year] = formatter.format(date).split("/");
			return `${month}_${year}`;
		});

		// if (filterValues.range === encodeURIComponent(parsedRange)) {
		// 	await refetch();
		// 	return;
		// }

		setFilterValues((prev) => ({
			...prev,
			page: 0,
			country: country,
			company: companyId,
			range: encodeURIComponent(parsedRange),
		}));
	};

	// Handle pagination changes
	useEffect(() => {
		setFilterValues((prev) => ({
			...prev,
			page: pagination.pageIndex,
			perPage: pagination.pageSize,
		}));
	}, [pagination]);

	if (isLoading || isFetching) {
		return (
			<Box>
				<Skeleton
					height={200}
					sx={{
						width: "100%",
						height: "100%",
						transform: "scale(1)",
					}}
				/>
			</Box>
		);
	}

	return (
		<Box>
			<Controls
				companyId={companyId}
				country={country}
				setCountry={setCountry}
				rangeValue={rangeValue}
				onRangeChange={setRangeValue}
				onSubmit={handleSubmit}
				isLoading={isLoading || isFetching}
			/>
			{error ? (
				<Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
					Wystąpił błąd podczas pobierania danych.
				</Box>
			) : (
				<>
					{stats?.data?.length > 0 ? (
						<>
							{stats?.data?.map((companyStats) => {
								return (
									<>
										<Grid container spacing={2} sx={{ mt: 4 }}>
											{!companyId && (
												<Grid item size={12}>
													<Box sx={{ display: "inline-flex" }}>
														<Link
															to={`/admin/company/${companyStats?.company?.id}`}
															asChild
															style={{
																textDecoration: "none",
																color: "inherit",
															}}
														>
															<Typography
																component="h2"
																variant="h4"
																sx={{ mb: 1, textDecoration: "none" }}
															>
																{companyStats?.company?.name}
															</Typography>
														</Link>
													</Box>
												</Grid>
											)}
											<Grid item size={12}>
												<Box>
													<Typography
														component="h2"
														variant="h5"
														sx={{ mb: 1 }}
													>
														Statystyki tygodniowe
													</Typography>
													<Table size="small">
														<TableHead>
															<TableRow>
																<TableCell>Zakres</TableCell>
																<TableCell align="center">
																	Liczba osób publikujących
																</TableCell>
																<TableCell align="center">
																	Ofert w sumie
																</TableCell>
																<TableCell
																	align="center"
																	sx={{ color: "#0069b4" }}
																>
																	Timocom
																</TableCell>
																<TableCell
																	align="center"
																	sx={{ color: "#f07c00" }}
																>
																	Trans
																</TableCell>
																<TableCell
																	align="center"
																	sx={{ color: "#ff9999" }}
																>
																	Teleroute
																</TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{companyStats.weeklyStats.length > 0 ? (
																companyStats.weeklyStats.map((w) => (
																	<TableRow key={w.from} hover>
																		<TableCell>{`${dayjs(w.from).format(
																			"DD MMM"
																		)} - ${dayjs(w.to).format(
																			"DD MMM"
																		)}`}</TableCell>
																		<TableCell align="center">
																			{w.total_publishers}
																		</TableCell>
																		<TableCell align="center">
																			{w.total_offers}
																		</TableCell>
																		<TableCell align="center">
																			{w.timocom_offers}
																		</TableCell>
																		<TableCell align="center">
																			{w.trans_offers}
																		</TableCell>
																		<TableCell align="center">
																			{w.teleroute_offers}
																		</TableCell>
																	</TableRow>
																))
															) : (
																<TableRow>
																	<TableCell colSpan={6} align="center">
																		Brak danych
																	</TableCell>
																</TableRow>
															)}
														</TableBody>
													</Table>
												</Box>
											</Grid>
											<Grid item size={6}>
												<Box>
													<Typography
														component="h2"
														variant="h5"
														sx={{ mb: 1 }}
													>
														DAU
													</Typography>
													<Table size="small">
														<TableHead>
															<TableRow>
																<TableCell>Data</TableCell>
																<TableCell align="center">DAU</TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{companyStats.dau.length > 0 ? (
																companyStats.dau.map((d) => (
																	<TableRow key={d.date} hover>
																		<TableCell>
																			{format(d.date, "dd.MM.yyyy")}
																		</TableCell>
																		<TableCell align="center">
																			{d.dau}
																		</TableCell>
																	</TableRow>
																))
															) : (
																<TableRow>
																	<TableCell colSpan={6} align="center">
																		Brak danych
																	</TableCell>
																</TableRow>
															)}
														</TableBody>
													</Table>
												</Box>
											</Grid>
											<Grid item size={6}>
												<Box>
													<Typography
														component="h2"
														variant="h5"
														sx={{ mb: 1 }}
													>
														MAU
													</Typography>
													<Table size="small">
														<TableHead>
															<TableRow>
																<TableCell>Data</TableCell>
																<TableCell align="center">MAU</TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{companyStats.mau.length > 0 ? (
																companyStats.mau.map((m) => {
																	const localeToUse =
																		localeMap[locale] || plLocale;
																	const date = parse(
																		m.month,
																		"yyyy-MM",
																		new Date()
																	);
																	const formatted = format(date, "LLLL yyyy", {
																		locale: localeToUse,
																	});

																	return (
																		<TableRow key={m.month} hover>
																			<TableCell>{formatted}</TableCell>
																			<TableCell align="center">
																				{m.mau}
																			</TableCell>
																		</TableRow>
																	);
																})
															) : (
																<TableRow>
																	<TableCell colSpan={6} align="center">
																		Brak danych
																	</TableCell>
																</TableRow>
															)}
														</TableBody>
													</Table>
												</Box>
											</Grid>
										</Grid>
									</>
								);
							})}
							{!companyId && (
								<TablePagination
									component="div"
									count={stats?.pagination.total} // Total number of rows in the table
									rowsPerPageOptions={[10, 20, 50, 100]} // Options for rows per page
									rowsPerPage={pagination.pageSize} // Current page size
									page={pagination.pageIndex} // Current page index (0-based)
									onPageChange={(event, newPage) => {
										setPagination((prev) => {
											return {
												...prev,
												pageIndex: newPage,
											};
										});
									}}
									onRowsPerPageChange={(event) => {
										setPagination((prev) => {
											return {
												...prev,
												pageSize: Number(event.target.value), // Update page size
												pageIndex: 0, // Reset to first page
											};
										});
									}}
									labelRowsPerPage="Wiersze na stronę"
									labelDisplayedRows={({ from, to, count }) =>
										`${from}–${to} z ${
											count !== -1 ? count : `więcej niż ${to}`
										}`
									}
								/>
							)}
						</>
					) : (
						<Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
							Brak danych dla wybranych filtrów.
						</Box>
					)}
				</>
			)}
		</Box>
	);
};

const Controls = ({
	companyId,
	country,
	setCountry,
	rangeValue,
	onRangeChange,
	onSubmit,
	isLoading,
}) => {
	const { data: countryCodes, isLoading: isLoadingCountryCodes } =
		useGetCountryCodesQuery();

	const todayYear = new Date().getFullYear();

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
			{!companyId && (
				<Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
					{isLoadingCountryCodes ? (
						<Skeleton
							width={127.5}
							height={36.71}
							sx={{
								transform: "scale(1)",
								marginTop: "16px",
								marginBottom: "8px",
							}}
						/>
					) : (
						<>
							<Typography
								variant="label"
								component="label"
								htmlFor="country-select"
							>
								Kraj:
							</Typography>
							<Select
								size="small"
								value={country}
								onChange={(e) => setCountry(e.target.value)}
								id="country-select"
							>
								{countryCodes.map((code) => {
									return (
										<MenuItem key={code} value={code}>
											{code}
										</MenuItem>
									);
								})}
							</Select>
						</>
					)}
				</Box>
			)}
			<Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
				<Typography variant="label" component="label" htmlFor="month-select">
					Miesiąc:
				</Typography>
				<Box sx={{ display: "flex", alignItems: "center", width: "200px" }}>
					<FormControl fullWidth margin="none">
						<YearMonthRangePicker
							onChange={onRangeChange}
							value={rangeValue}
							minDate={new Date(2020, 0)}
							maxDate={new Date(todayYear, 0)}
						/>
					</FormControl>
				</Box>
			</Box>
			<Box>
				<Button
					loading={isLoading}
					onClick={onSubmit}
					type="button"
					fullWidth
					variant="contained"
				>
					Zmień
				</Button>
			</Box>
		</Box>
	);
};
